import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AccessLevel, RoleLevel } from '../user/user.model';
import { RestfulEndpoint } from '../../services/restful-endpoint.service';
import { PersonalizationService } from '../../services/personalization.service';
import { environment } from '../../../environments/environment';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { OAuthService } from './oauth.service';
import { KeycloakService } from './keycloak.service';

@Injectable()
export class AuthService {
  private _authenticated: boolean = false;
  private _signingOut: boolean = false;
  private static readonly LOGIN_REDIRECT_PARAM_KEYS = [
    'token',
    'access_token',
    'id_token',
    'Accept-Language',
    'accept-language',
  ] as const;

  constructor(
    private endPoint: RestfulEndpoint,
    private _http: HttpClient,
    public _persona: PersonalizationService,
    private userService: UserService,
    private _router: Router,
    private transloco: TranslocoService,
    private oauth: OAuthService,
    private keycloakService: KeycloakService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Encoder And Decoder
  // -----------------------------------------------------------------------------------------------------
  // Function to encode a string to Base64
  encode(value: string): string {
    return btoa(value); // Convert to Base64
  }

  // Function to decode a Base64 string
  decode(value: string): string {
    return atob(value); // Decode Base64
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get accessToken(): string {
    return localStorage.getItem('access_token') ?? '';
  }

  get logoutAttempts(): string {
    const encodedKey = this.encode('logout_attempts');
    const attempts = localStorage.getItem(encodedKey);

    // Decode the value if it exists, otherwise default to 3 attempts
    return attempts ? this.decode(attempts) : '3';
  }

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem('access_token', token);
  }

  set logoutAttempts(attempts: string) {
    localStorage.setItem(this.encode('logout_attempts'), this.encode(attempts)); // Store encoded value
  }

  get version(): string {
    return localStorage.getItem('version') ?? '';
  }

  set version(version: string) {
    localStorage.setItem('version', version);
  }

  get bimPermission(): boolean {
    const userData = JSON.parse(localStorage.getItem('user-data'));
    return userData.enable_bim === 1;
  }

  get pledgeState(): any {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    const isEnabled = userData.pledge_message_enabled ?? false;
    return isEnabled ? true : false;
  }

  set pledgeState(state: boolean) {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    userData.pledge_message_enabled = state;
    localStorage.setItem('user-data', JSON.stringify(userData));
  }

  get emailLanguage(): any {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    const lang = userData?.preferences?.language;
    return lang;
  }

  set emailLanguage(lang: string) {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    userData.preferences = { ...(userData.preferences ?? {}), language: lang };
    localStorage.setItem('user-data', JSON.stringify(userData));
  }

  get pledgeDate(): any {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    const date = userData.photo_security_pledge_agreed_updated_at ?? 'N/A';
    return date;
  }

  set pledgeDate(date: string) {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    userData.photo_security_pledge_agreed_updated_at = date;
    localStorage.setItem('user-data', JSON.stringify(userData));
  }

  get pledgeStateAgreement(): any {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    const isEnabled = userData.photo_security_pledge_agreed ?? false;
    return isEnabled ? true : false;
  }

  set pledgeStateAgreement(state: boolean) {
    const userData = JSON.parse(localStorage.getItem('user-data')) || {};
    userData.photo_security_pledge_agreed = state;
    localStorage.setItem('user-data', JSON.stringify(userData));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return this.endPoint.post('request_change_password', { email });
    // return this._httpClient.post('api/auth/forgot-password', email);
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    return this.endPoint.post('change_old_password', {
      old_password: oldPassword,
      password: newPassword,
      c_password: newPassword,
    });
    // return this._httpClient.post('api/auth/forgot-password', email);
  }

  /**
   * Reset password
   *
   * @param password
   * @param user_id
   */
  resetPassword(password: string, user_id: string): Observable<any> {
    return this.endPoint.post('create_new_password', {
      password,
      c_password: password,
      user_id,
    });
  }

  /**
   * Reset password
   *
   * @param otp
   * @param user_id
   */
  validateOtp(otp: string, user_id: string): Observable<any> {
    return this.endPoint.post('verify_change_password', { code: otp, user_id });
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return this.endPoint.post(`sign_in`, credentials).pipe(
      switchMap((response: any) => {
        if (!response.otp_required) {
          // Store the access token in the local storage
          this.accessToken = response?.user_profile?.single_access_token;
          this.logoutAttempts = '3';
          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          // this._userService.user = response.user_profile;
          // this.userData = response.user_profile;
          // this._userService.user = response.user_profile;

          // New Personalization Flow
          if (response.user_profile.personalization_flag) {
            this._persona.usePersona = true;
            this._persona.init(
              response.user_profile.personalization,
              response.user_profile.personalization.secondary_name ?? null,
            );
          } else {
            this._persona.resetThemeToDefault(
              response.user_profile.personalization?.secondary_name ?? null,
            );
          }
        }
        return of(response);
      }),
    );
  }

  verifyOtp(otp: string, user_id: string): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return this.endPoint.post(`verify_otp`, { code: otp, user_id }).pipe(
      switchMap((response: any) => {
        if (!response.otp_required) {
          // Store the access token in the local storage
          this.logoutAttempts = '3';
          this.accessToken = response?.user_profile?.single_access_token;

          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          // this._userService.user = response.user_profile;
          // this.userData = response.user_profile;

          // save user personalization
          // New Personalization Flow
          if (response.user_profile.personalization_flag) {
            this._persona.usePersona = true;
            this._persona.init(
              response.user_profile.personalization,
              response.user_profile.personalization.secondary_name ?? null, // Ensure secondary_name is passed if available
            );
          } else {
            this._persona.resetThemeToDefault(
              response.user_profile.personalization?.secondary_name ?? null, // Ensure secondary_name is passed even when defaulting
            );
          }
        }
        // Return a new observable with the response
        return of(response);
      }),
    );
  }

  /*Update pledge agreement*/
  updatePledgeAgreement(data: {
    photo_security_pledge_agreed: boolean;
  }): Observable<{ message: string }> {
    return this.endPoint.post('update_photo_security_pledge_agreed', data);
  }

  resendOtp(user_id: string): Observable<any> {
    return this.endPoint.post(`resend_otp`, { user_id });
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Renew token

    // todo check Login Expire time
    // Set the authenticated flag to true
    this._authenticated = true;

    // Store the user on the user service
    // this._userService.user = this.userData;

    // Return true
    return of(true);

    /*   return this._httpClient.post('api/auth/refresh-access-token', {
           access_token: this.accessToken
       }).pipe(
           catchError(() => {

               // Return false
               return of(false);
           }),
           switchMap((response: any) => {

               // Store the access token in the local storage
               this.accessToken = response.access_token;

               // Set the authenticated flag to true
               this._authenticated = true;

               // Store the user on the user service
               this._userService.user = response.user;

               this.userData = response.user;
               // Return true
               return of(true);
           })
       );*/
  }

  /**
   * Check if currently signing out (to prevent loops)
   */
  get isSigningOut(): boolean {
    return this._signingOut;
  }

  /**
   * Sign out. Clears local state and redirects immediately so the user sees instant logout
   * (e.g. after changing language). Logout API is called in the background without blocking.
   */
  async signOut() {
    // Prevent re-entry (the interceptor calls signOut on 401 which can loop)
    if (this._signingOut) {
      return;
    }
    this._signingOut = true;

    // Capture values BEFORE clearing state
    const lang = localStorage.getItem('lang') || 'en';
    const deviceId = localStorage.getItem('deviceId') || '';
    const notificationToken = localStorage.getItem('fcm_token') || '';
    // Save token before clearing - we'll use it for the logout API call
    const savedToken = this.oauth.accessToken || this.accessToken;
    // Capture id_token before clearLocalAuthState() removes it. Without it,
    // keycloak-js omits id_token_hint from the logout URL and Keycloak shows
    // its "Do you want to log out?" confirmation page.
    const idToken = localStorage.getItem('id_token') || '';

    // Clear OAuth token from MEMORY so interceptor won't add Authorization header to new requests.
    this.oauth.accessToken = '';
    this._authenticated = false;

    // Build logout payload from already-captured values (before localStorage is cleared)
    let logoutPayload = {};
    if (deviceId && notificationToken) {
      logoutPayload = {
        device_id: deviceId || null,
        notification_token: notificationToken || null,
      };
    }

    // Manually add Authorization header since we cleared OAuth token from memory
    const logoutHeaders = savedToken
      ? new HttpHeaders({
          Authorization: `Bearer ${savedToken}`,
        })
      : undefined;

    await this.revokeKeycloakToken(savedToken).finally(() => {});

    // Clear localStorage immediately after revoking the token.
    // This prevents the loop where login-stg auto re-logs in via SSO and redirects back
    // with a new access_token in the URL, which applyLoginRedirectParamsFromUrl() would store.
    await this.clearLocalAuthState();

    this.endPoint.post('v2/users/logout', logoutPayload, logoutHeaders).subscribe({
      next: () => {
        this.clearLocalAuthState();
        // then navigate to sign-out page for countdown before redirect
        // this.stripAuthParamsFromUrl();
        this.redirectToLogin(lang, idToken);
      },
      error: () => {
        // Even if logout API fails (e.g., 401), still complete the sign out process
        // this.stripAuthParamsFromUrl();
        this.redirectToLogin(lang, idToken);
      },
    });
  }

  /**
   * Revoke the Keycloak access token using x-www-form-urlencoded format.
   * This calls the Keycloak revoke endpoint directly (bypassing the oauth library
   * which sends JSON and doesn't clear localStorage).
   */
  private revokeKeycloakToken(token: string): Promise<any> {
    if (!token) {
      return Promise.resolve();
    }
    const { origin, realm, client_id } = environment.oauth;
    const url = `${origin}/realms/${realm}/protocol/openid-connect/revoke`;
    const body = new URLSearchParams({ client_id, token });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this._http
      .post(url, body.toString(), { headers })
      .toPromise()
      .catch((err) => {
        console.log('[signOut] Keycloak revoke error:', err);
      });
  }

  /**
   * Remove token and Accept-Language from the current URL so that after reload
   * applyLoginRedirectParamsFromUrl() does not re-apply the token (fixes first logout
   * after language change ending up back on all projects when coming from SSO).
   */
  private stripAuthParamsFromUrl(): void {
    const url = new URL(window.location.href);
    AuthService.LOGIN_REDIRECT_PARAM_KEYS.forEach((key) => url.searchParams.delete(key));
    url.hash = this.stripParamsFromHash(url.hash);
    const cleanUrl = url.pathname + (url.search || '') + (url.hash || '');
    window.history.replaceState(null, '', cleanUrl);
  }

  public clearTokenParamsFromUrl(_url: string): string {
    if (!_url) {
      return _url;
    }

    const isUrl = _url.startsWith('http');
    const paramsToStrip = AuthService.LOGIN_REDIRECT_PARAM_KEYS;

    if (isUrl) {
      try {
        const url = new URL(_url);
        paramsToStrip.forEach((key) => url.searchParams.delete(key));
        url.hash = this.stripParamsFromHash(url.hash);
        const cleanUrl = url.origin + url.pathname + (url.search || '') + (url.hash || '');

        return cleanUrl;
      } catch {
        return _url;
      }
    } else if (_url) {
      const [withoutHash, hash = ''] = _url.split('#', 2);
      const [pathname, search = ''] = withoutHash.split('?', 2);
      const params = new URLSearchParams(search);
      paramsToStrip.forEach((key) => params.delete(key));

      const newSearch = params.toString();
      const cleanedHash = this.stripParamsFromHash(hash ? `#${hash}` : '');
      return `${pathname}${newSearch ? `?${newSearch}` : ''}${cleanedHash || ''}`;
    }

    return _url;
  }

  private stripParamsFromHash(hash: string): string {
    if (!hash) {
      return '';
    }

    const hashWithoutPrefix = hash.startsWith('#') ? hash.slice(1) : hash;

    const hasPathSegment = hashWithoutPrefix.includes('/');
    const hasHashQueryPath = hashWithoutPrefix.includes('?');
    const containsAuthFragmentParam = AuthService.LOGIN_REDIRECT_PARAM_KEYS.some(
      (key) => hashWithoutPrefix.startsWith(`${key}=`) || hashWithoutPrefix.includes(`&${key}=`),
    );

    // Handles OAuth implicit-flow style fragments:
    // #access_token=...&id_token=...&Accept-Language=...
    if (
      !hasPathSegment &&
      !hasHashQueryPath &&
      hashWithoutPrefix.includes('=') &&
      containsAuthFragmentParam
    ) {
      const hashParams = new URLSearchParams(hashWithoutPrefix);
      AuthService.LOGIN_REDIRECT_PARAM_KEYS.forEach((key) => hashParams.delete(key));
      const cleanedFragment = hashParams.toString();
      return cleanedFragment ? `#${cleanedFragment}` : '';
    }

    const queryIndex = hashWithoutPrefix.indexOf('?');
    if (queryIndex === -1) {
      return `#${hashWithoutPrefix}`;
    }

    const hashPath = hashWithoutPrefix.slice(0, queryIndex);
    const hashQuery = hashWithoutPrefix.slice(queryIndex + 1);
    const hashParams = new URLSearchParams(hashQuery);
    AuthService.LOGIN_REDIRECT_PARAM_KEYS.forEach((key) => hashParams.delete(key));

    const cleanHashQuery = hashParams.toString();
    return `#${hashPath}${cleanHashQuery ? `?${cleanHashQuery}` : ''}`;
  }

  /**
   * Clear local auth state only (no reload). Use before navigate when intending to show sign-out page.
   * For "Sign Out from All Devices" success, use clearLocalAuthAndReload() so the app reloads without token and redirects to sign-in (PC-1632).
   */
  async clearLocalAuthState(): Promise<void> {
    localStorage.removeItem('bim360AccessToken');
    localStorage.removeItem('deviceId');
    localStorage.removeItem('fcm_token');
    this._authenticated = false;
    this.accessToken = '';
    // Clear OAuth token in memory and localStorage (setAccessToken writes '' then removeItem ensures the key is fully gone)
    this.oauth.setAccessToken('');
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
  }

  /**
   * Clear local auth state and reload. Use after successful "Sign Out from All Devices" so the user is not left with a valid token (which caused redirect loop to /projects/all_projects for V1 users - PC-1632).
   */
  async clearLocalAuthAndReload(): Promise<void> {
    this._signingOut = true;
    const lang = localStorage.getItem('lang') || 'en';
    this.oauth
      .revokeToken()
      .catch(() => {})
      .finally(() => {
        this.clearLocalAuthState();
        this.stripAuthParamsFromUrl();
        this.redirectToLogin(lang);
      });
  }

  /** SessionStorage key for intended URL (must match AuthGuard.STORED_REDIRECT_URL_KEY). */
  private static readonly STORED_REDIRECT_URL_KEY = 'auth_redirect_url';

  /**
   * Redirect to the login page after signing out.
   * Two-way logout: revoke (already done by caller) then redirect to login app. No Keycloak logout URL.
   * Preserves redirectURL in all cases: current path + query, or fallback to sessionStorage (set by guard).
   */
  private redirectToLogin(lang: string, idToken?: string): void {
    // Primary: use Keycloak end-session so the Keycloak session is terminated
    // and the user is returned to the sign-in flow (which will trigger PKCE again).
    if (this.keycloakService.isEnabled()) {
      // Reset personalization theme right before navigating away (PC-1783)
      this._persona.resetThemeToDefault();
      const postLogoutRedirectUri = `${window.location.origin}/sign-in?accept-language=${lang}&logout=true`;
      this.keycloakService.logout(postLogoutRedirectUri, idToken).catch((err) => {
        console.error('[redirectToLogin] Keycloak logout redirect failed', err);
        // Fallback: force navigate to sign-in within the SPA
        this._router.navigate(['/sign-in']);
      });
      return;
    }

    // Legacy fallback: external login app redirect (when new_login_url set, Keycloak not enabled)
    if (environment.new_login_url) {
      const separator = environment.new_login_url.includes('?') ? '&' : '?';
      let loginUrl = `${environment.new_login_url}${separator}accept-language=${lang}&logout=true`;
      loginUrl = this.clearTokenParamsFromUrl(loginUrl);

      const currentPath =
        typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';
      const fromStorage =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem(AuthService.STORED_REDIRECT_URL_KEY)
          : null;
      let redirectURL = '';
      if (
        currentPath &&
        currentPath !== '/' &&
        !currentPath.startsWith('/sign-in') &&
        !currentPath.startsWith('/sign-out')
      ) {
        redirectURL = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
      }
      if (
        !redirectURL &&
        fromStorage &&
        fromStorage !== '/' &&
        !fromStorage.startsWith('/sign-in') &&
        !fromStorage.startsWith('/sign-out')
      ) {
        redirectURL = fromStorage.startsWith('/') ? fromStorage : `/${fromStorage}`;
      }

      if (redirectURL) {
        redirectURL = this.clearTokenParamsFromUrl(redirectURL);
        loginUrl += `&redirectURL=${encodeURIComponent(redirectURL)}`;
      }

      // Reset personalization theme right before navigating away (PC-1783)
      this._persona.resetThemeToDefault();
      window.location.replace(loginUrl);
      return;
    }

    // Local dev: navigate to sign-out page for countdown
    this._persona.resetThemeToDefault();
    this._router.navigate(['/sign-out'], { queryParams: { lang } });
  }

  signOutFromAll(): Observable<any> {
    return this.endPoint.post('logout', { token: this.accessToken });
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: {
    name: string;
    email: string;
    password: string;
    company: string;
  }): Observable<any> {
    return this.endPoint.post('auth/sign-up', user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this.endPoint.post('auth/unlock-session', credentials);
  }

  /**
   * Check the permission
   */
  async getPermission(access: AccessLevel) {
    const user = await this.userService.getCurrentUser();
    return user.permissions.includes(access);
  }

  /**
   * Check the role
   */
  async getRole(role: RoleLevel) {
    const user = await this.userService.getCurrentUser();
    return user.permissions.includes(role);
  }

  /**
   * Check the authentication status
   */
  /**
   * Apply token and Accept-Language from current URL when returning from login app redirect.
   * Call this early (e.g. in auth guard) so /projects/...?access_token=...&Accept-Language=ar is accepted.
   * @returns true if URL contained access_token/token or Accept-Language (so caller can clean URL after)
   */
  applyLoginRedirectParamsFromUrl(): boolean {
    const url = new URL(window.location.href);
    // console.log('applyLoginRedirectParamsFromUrl', url);
    const token = url.searchParams.get('token') || url.searchParams.get('access_token');
    const idToken = url.searchParams.get('id_token');
    const acceptLang =
      url.searchParams.get('Accept-Language') || url.searchParams.get('accept-language');
    if (token) {
      this.accessToken = token;
      // Sync to OAuthService so the auth interceptor adds Authorization header for /me and other API calls
      this.oauth.setAccessToken(token);
    }
    if (idToken) {
      localStorage.setItem('id_token', idToken);
    }
    if (acceptLang && ['en', 'ar', 'ru'].includes(acceptLang)) {
      localStorage.setItem('lang', acceptLang);
      this.transloco.setActiveLang(acceptLang);
    }
    const hadAuthParams = !!(token || acceptLang || idToken); // Strip auth params from URL immediately so a refresh does not re-apply the token (avoids
    // "first clear+refresh re-injects token, second clear+refresh goes to login" behavior).
    if (hadAuthParams && typeof window !== 'undefined') {
      this.stripAuthParamsFromUrl();
    }
    return hadAuthParams;
  }

  check(caller = 'unknown'): Observable<boolean> {
    console.log('[check] caller', caller);
    const currentURL = window.location.href;
    const url = new URL(currentURL);
    const token = url.searchParams.get('token') || url.searchParams.get('access_token');

    // When returning from login app with token, apply Accept-Language from URL so language is persisted
    const hasRedirectParams = AuthService.LOGIN_REDIRECT_PARAM_KEYS.some((key) =>
      url.searchParams.has(key),
    );
    if (hasRedirectParams) {
      this.applyLoginRedirectParamsFromUrl();
    }

    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check if a token exists in the URL
    if (token) {
      // Store the access token in the local storage
      this.accessToken = token;

      // Set the authenticated flag to true
      this._authenticated = true;

      // Call the API to get user data using the token
      return this.checkToken(token).pipe(
        switchMap((response: any) => {
          // Store the user on the user service
          // this._userService.user = response.user_profile;

          // Store the user data in the local storage
          // this.userData = response.user_profile;

          // New Personalization Flow
          if (response.user_profile.personalization_flag) {
            this._persona.usePersona = true;
            this._persona.init(
              response.user_profile.personalization,
              response.user_profile.personalization.secondary_name ?? null,
            );
          } else {
            this._persona.resetThemeToDefault(
              response.user_profile.personalization?.secondary_name ?? null,
            );
          }

          // Return true
          return of(true);
        }),
        catchError(() => {
          // Remove the access token from the local storage
          localStorage.removeItem('access_token');
          // Set the authenticated flag to false
          this._authenticated = false;
          this.signOut();
          window.location.href = `${environment.base_url}`;
          // Return false
          return of(false);
        }),
      );
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // If the access token exists, sign in using it
    if (!token) {
      return this.signInUsingToken();
    }
  }

  checkToken(token: string): Observable<any> {
    this.accessToken = token;
    // Set the authenticated flag to true
    this._authenticated = true;
    return this.endPoint.post(`me`, null);
  }

  async getUpdated() {
    const user = await this.userService.getCurrentUser();

    // New Personalization Flow
    if (user.personalization_flag) {
      this._persona.usePersona = true;
      this._persona.init(user.personalization, user.personalization.secondary_name ?? null);
    } else {
      this._persona.resetThemeToDefault(user.personalization?.secondary_name ?? null);
    }
  }

  async logOutAllDevices(cred: { password: string }): Promise<logOutAllDevicesResponse> {
    const projectBody = new FormData();
    projectBody.append('password', cred.password);

    return new Promise((resolve, reject) => {
      this.api_logOutAllDevices(projectBody).subscribe(
        (projects: logOutAllDevicesResponse) => {
          resolve(projects);
          localStorage.removeItem('deviceId');
          localStorage.removeItem('fcm_token');
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  private api_logOutAllDevices(body: any): any {
    return this.endPoint.post('logout-all-devices', body, null, 'users');
  }
}

export interface logOutAllDevicesResponse {
  status: string;
  message: string;
  meta_data: [];
  data: any;
  errors: [];
}
